import React, { useState, useEffect, useRef } from "react";
import CookieConsent from "./CookieConsent";
import ConsultationModal from "./ConsultationModal";
import "./StudioHome.css";
import "./StudioRefinement.css";
import "./StudioVisualSections.css";
import "./ServiceExamples.css";
import "./StudioPolish.css";
import "./StudioExamplesFinal.css";
import "./StudioTypography.css";
import { UXComparison, AIWorkflow } from "./ServiceExamples";

import UXSeries from "./UXSeries";
import { StudioServiceTabs, StudioTrust, StudioFAQ } from "./StudioRestored";
import "./StudioRestored.css";

export default function StudioHome() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(96);
  useEffect(() => {
    const header = headerRef.current;
    const measure = () => setHeaderHeight(header.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  const [booking, setBooking] = useState(null);
  const [needs, setNeeds] = useState([]);
  const options = ["An idea or MVP to build", "Help with an existing product", "A UX audit or ongoing UX support", "AI training or business integrations"];
  const [menu, setMenu] = useState(false);
  const open = (type) => {
    setMenu(false);
    setBooking(type);
  };
  return (
    <div className="studio-home" style={{ "--studio-header-height": `${headerHeight}px` }}>
      <a className="studio-skip" href="#main">
        Skip to content
      </a>
      <header className="studio-header" ref={headerRef}>
        <a className="studio-brand" href="/">
          <img src="/images/logo.webp" alt="" />
          Second Life Software
        </a>
        <button
          className="studio-menu"
          onClick={() => setMenu(!menu)}
          aria-expanded={menu}
          aria-controls="studio-nav"
          aria-label={menu ? "Close menu" : "Open menu"}
        >
          <span className="hamburger-lines" aria-hidden="true"><i /><i /><i /></span>
        </button>
        <nav
          id="studio-nav"
          className={menu ? "is-open" : ""}
          aria-label="Main navigation"
        >
          <a href="#work" onClick={() => setMenu(false)}>
            What we do
          </a>
          <a href="#ux-audits" onClick={() => setMenu(false)}>
            UX/UI audits
          </a>
          <a href="#business-ai" onClick={() => setMenu(false)}>Business AI</a>
          <a href="#about" onClick={() => setMenu(false)}>
            About
          </a>
          <a href="https://secondlifesoftware.com/blog">Blog</a>
          <a href="https://secondlifesoftware.com/design/" target="_blank" rel="noopener noreferrer">Design</a>
          <button
            className="studio-button dark"
            onClick={() => open("project")}
          >
            Let’s talk
          </button>
        </nav>
      </header>
      <main id="main">
        <section className="studio-hero">
          <div className="studio-hero-copy">
            <h1>
              We offer software development,<br /> UX audits & AI integrations<br /> for businesses
            </h1>
            <p>
              Turn an idea into a production-ready product.<br />
              Development, security, UX, and AI where it makes sense.
            </p>
            <div className="studio-actions">
              <button
                className="studio-button gold"
                onClick={() => open("project")}
              >
                Book a project call
              </button>
              <button className="studio-button" onClick={() => open("audit")}>
                Book a UX audit consultation
              </button>
            </div>
            <small className="studio-intro-note">
              Start with a free 15-minute introduction.
            </small>
          </div>
        </section>
        <section className="consultation-intake studio-section">
          <fieldset className="needs-checklist"><legend>If any of these sound like you, we can help</legend><p>Select what you’re looking for. We’ll include it with your consultation inquiry.</p><div className="needs-options">{options.map(label => <label key={label}><input type="checkbox" checked={needs.includes(label)} onChange={() => setNeeds(current => current.includes(label) ? current.filter(item => item !== label) : [...current, label])}/><span>{label}</span></label>)}</div><button className="checklist-submit" onClick={() => open(needs.length === 1 && needs[0] === options[2] ? "audit" : needs.length === 1 && needs[0] === options[3] ? "ai" : "project")}>Book time</button></fieldset>
        </section>
        <section id="ux-audits" className="studio-audit studio-section">
          <div>
            <h2>Make your product easier to use</h2>
            <p>
              Help people find what they need, complete tasks, and move through your app with less friction. We review the experience on web or mobile and recommend specific improvements.
            </p>


            <button
              className="studio-button gold"
              onClick={() => open("audit")}
            >
              Book a UX audit consultation
            </button>
            <small>
              The consultation is free. We’ll agree on the audit scope and price
              first.
            </small>
          </div>
          <UXComparison />
          <div className="audit-outcome"><article><h3>One-time UX audit</h3><p>A review of agreed screens and user journeys, documented usability findings, and prioritized recommendations for your team.</p></article><article><h3>Ongoing UX support</h3><p>Regular reviews of designs and working software, feedback on new flows, and follow-up checks as your team makes changes.</p></article></div>
        </section>
        <UXSeries />
        <StudioTrust />
        <section id="work" className="studio-section studio-work">
          <h2>What we build and how we help</h2>
          <StudioServiceTabs />
        </section>
        <section className="studio-section studio-services">

          <article>
            <span id="founders" className="studio-eyebrow">For founders & new ideas</span>
            <h2>You don’t need to have it all figured out</h2>
            <p>Bring the idea. We’ll help define the first version, choose the technology, design the experience, and build it through to production.</p>
            <p>
              Already funded or building? We can work alongside your team on development, UX, and security, with a scope that fits your stage.
            </p>
            <button
              className="studio-text-button"
              onClick={() => open("project")}
            >
              Book a free founder consultation
            </button>
          </article>
          <article>
            <span id="product-support" className="studio-eyebrow">For existing products</span>
            <h2>Experienced help with the product you already have</h2>
            <p>
              Bring us in to take over a codebase, support your engineers, or
              ship the next feature.
            </p>
            <p>
              From the first idea and MVP to taking over from another team or adding extra hands, we can help at any stage. You work with our tight-knit team, with no offshore outsourcing.
            </p>
            <button
              className="studio-text-button"
              onClick={() => open("project")}
            >
              Discuss your project
            </button>
          </article>
        </section>
        <section id="business-ai" className="studio-section business-ai"><div><h2>Make the right call<br />on AI</h2><p>Not every workflow needs AI. We help you choose where to use it, how to integrate it, and what to spend.</p><button className="studio-button gold" onClick={() => open("ai")}>Book an AI consultation</button></div><AIWorkflow /><div className="ai-offerings"><article><h3>Strategy & tool selection</h3><p>Review your workflows and choose the right mix of AI tools, custom software, and classic automation.</p></article><article><h3>Integration & implementation</h3><p>Connect AI or rule-based automation to your business systems, with appropriate data access and human review.</p></article><article><h3>Cost & efficiency</h3><p>Review models, usage, and repetitive tasks to reduce avoidable AI spend as adoption grows. Evaluate cost savings against output quality.</p></article><article><h3>Training for your team</h3><p>Practical guidance on the tools and workflows your people will actually use.</p></article></div></section>
        <section id="about" className="studio-section studio-founders">
          <h2>Meet Darius & Katia</h2>
          <p className="founders-intro">
            A husband-and-wife team bringing product design, software
            engineering, and technical leadership to your project.
          </p>
          <article className="studio-founder">
            <img
              src="/images/darius.webp"
              alt="Darius, co-founder, lead engineer and cybersecurity expert"
              loading="lazy"
            />
            <div>
              <span className="studio-eyebrow">
                Co-founder, Lead Engineer & Cybersecurity Expert
              </span>
              <h3>Darius</h3>
              <p>
                Darius brings over a decade in technology, from cybersecurity in the defense sector to software engineering and technical program management at Microsoft. He has also led networking work at Emory University and Hospital.
              </p>
              <p>
                With a software engineering degree and a Master’s in Cybersecurity, he combines application development, security, and project planning. He stays hands-on, from technical decisions through implementation.
              </p>
            </div>
          </article>
          <article className="studio-founder">
            <img
              src="/images/katia.webp"
              alt="Katia, co-founder, user experience analyst and software engineer"
              loading="lazy"
            />
            <div>
              <span className="studio-eyebrow">
                Co-founder, User Experience Analyst & Software Engineer
              </span>
              <h3>Katia</h3>
              <p>
                Katia spent five years as a software engineer at Microsoft after working at startup Fluentstream. She builds web and mobile applications across a broad range of languages and technologies.
              </p>
              <p>
                She pairs full-stack engineering with UX analysis and a strong eye for design. From interface decisions to implementation, she makes complex technology feel approachable and easier to use.
              </p>
              <button
                className="studio-text-button"
                onClick={() => open("audit")}
              >
                Book a UX audit consultation with Katia
              </button>
            </div>
          </article>
        </section>
        <aside className="studio-team-promise"><strong>US-based. No overseas outsourcing.</strong><p>Every line of code, every design decision, every conversation. That’s us.</p></aside>
        <StudioFAQ />
      </main>
      <footer className="studio-footer">
        <a className="studio-brand" href="/">
          <img src="/images/logo.webp" alt="" />
          Second Life Software
        </a>
        <div>
          <a href="https://secondlifesoftware.com/blog">Blog</a>
          <a href="https://secondlifesoftware.com/design/" target="_blank" rel="noopener noreferrer">Design</a>
          <a href="#faq">FAQ</a>
          <a href="/privacy.html">Privacy</a>
          <a href="/terms.html">Terms</a>
          <a href="mailto:info@secondlifesoftware.com">Get in touch</a>
        </div>
        <small>© {new Date().getFullYear()} Second Life Software</small>
      </footer>
      {booking && (
        <ConsultationModal type={booking} interests={needs} onClose={() => setBooking(null)} />
      )}
      <CookieConsent />
    </div>
  );
}
